// Third-party
import React from 'react';
import moment from 'moment';
import { isEqual } from 'lodash';

// Utils
import { enumerateJSX, truncateString } from '../../../utils/objects';
import { formatMessage, formatLocalizedString, FormattedMessage } from '../../../utils/i18n';
import { getLatestPriceForHolding } from '../../../utils/calculation-utils';
import Dictionary from '@/types/dictionary';
import PortfolioHolding from '@/types/portfolio-holding';
import Holding from '@/types/holding';

type Props = {
  holdings: PortfolioHolding[];
  holdingsData: Dictionary<Holding>;
};

class PortfolioHoldingPricesDisclaimer extends React.Component<Props> {
  shouldComponentUpdate(nextProps: Props) {
    return !isEqual(this.props, nextProps);
  }

  formatDate = (date: Date) => moment.utc(date).format(formatMessage('Common.DateFormats.Short'));

  getNameElement = (holding: PortfolioHolding) => {
    let label = truncateString(formatLocalizedString(holding.name), 40);

    if (holding.symbol) {
      label = holding.symbol;
    }

    return (
      <span key={holding.original.id} className="item-name" data-private data-dd-privacy="mask">
        {label}
      </span>
    );
  };

  render() {
    const { holdings, holdingsData } = this.props;

    // Units are only accessible in the context of portfolios.
    // `holdings` can only be PortfolioHolding[].
    const exceptions = (holdings as PortfolioHolding[]).reduce(
      (acc, holding) => {
        if (!holding.original.id) {
          return acc;
        }

        const data = holdingsData[holding.original.id];
        if (!data) {
          return acc;
        }

        const priceData = getLatestPriceForHolding(holding.original.type, data);
        if (!priceData) {
          return acc;
        }

        // If the price dates from longer than the last week's close, record it as an exception.
        const previousFriday = moment().day(moment().day() >= 5 ? 5 : -2);
        if (previousFriday.diff(moment(priceData[0]), 'days') > 1) {
          if (!acc[priceData[0]]) {
            acc[priceData[0]] = [];
          }

          acc[priceData[0]].push(holding);
        }

        return acc;
      },
      {} as { [key: number]: PortfolioHolding[] },
    );

    return (
      <div className="dates-disclaimer disclaimer">
        <FormattedMessage id="Disclaimers.PortfolioHoldingPrices.WeeklyPrices" />

        {Object.keys(exceptions).length > 0 && (
          <span>
            {` ${formatMessage('Disclaimers.PortfolioHoldingPrices.Exceptions')}`}
            {enumerateJSX(
              Object.keys(exceptions).map(key => {
                const date = Number(key);
                return (
                  <>
                    {` ${formatMessage('Disclaimers.EndingOn')} ${this.formatDate(new Date(date))}`}
                    {` ${formatMessage('Common.For')} `}
                    {enumerateJSX(exceptions[date].map(this.getNameElement), formatMessage('Common.And'))}
                  </>
                );
              }),
              formatMessage('Common.And'),
            )}
            .
          </span>
        )}
      </div>
    );
  }
}

export default PortfolioHoldingPricesDisclaimer;
